import React, { useEffect, useMemo, useState } from 'react';
import { INK } from '../../Utils/styleTokens';
import { uiLabel, KNOWN_ENTITY_CODES } from '../../Utils/Labeling';
import { ENTITY_LEVELS, LEVEL_NORMAL } from '../../Utils/entityLevels';
import { DEFAULT_ENTITY_PRIORITY } from '../../Utils/entityPriority';
import { getAdvancedConfig, saveAdvancedConfig } from '../api';

// Build the initial priority list: the saved order (if any) else the shipped default, then
// append any known entity codes the list is missing so every entity is reorderable.
const buildInitialOrder = (saved) => {
  const base = Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_ENTITY_PRIORITY;
  const seen = new Set();
  const out = [];
  base.forEach((code) => {
    if (typeof code === 'string' && code.trim() && !seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  });
  KNOWN_ENTITY_CODES.forEach((code) => {
    if (!seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  });
  return out;
};

const LEVEL_HELP = {
  NORMAL: 'Shown in reports and included in exports.',
  REPORT_ONLY: 'Shown in UI reports, excluded from exports.',
  HIDDEN: 'Hidden from reports and excluded from exports.',
};

// Entity Levels screen: set an install-wide display level per entity type. Front-end only —
// this changes what every user sees/exports; it does not affect discovery or masking runtime.
const EntityLevelsScreen = ({ canEdit }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // levels: { CODE: 'REPORT_ONLY' | 'HIDDEN' } — NORMAL omitted.
  const [levels, setLevels] = useState({});
  // Keep the algorithm-map override so a save here doesn't clobber it (shared config blob).
  const [algoOverride, setAlgoOverride] = useState({});
  // Preserved on save — the backend does a full replace, so we must round-trip sibling fields.
  const [maxRowsPerTable, setMaxRowsPerTable] = useState(null);
  // Preserved on save (full-replace PUT). Tri-state: null = unset -> worker default.
  const [scanEmbeddedStructures, setScanEmbeddedStructures] = useState(null);
  // Preserved on save (full-replace PUT) so reordering here never silently pins the Profile /
  // Schema Metadata feature back off. Default-on: only an explicit false hides it.
  const [profileEnabled, setProfileEnabled] = useState(true);
  const [filter, setFilter] = useState('');
  // Ordered entity-code list — "most important PII first" for the landscape entity tiles.
  const [order, setOrder] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await getAdvancedConfig();
        if (!alive) return;
        setLevels(cfg.entityLevels || {});
        setAlgoOverride(cfg.entityTypeToAlgorithm || {});
        setMaxRowsPerTable(cfg.maxRowsPerTable != null ? cfg.maxRowsPerTable : null);
        setScanEmbeddedStructures(
          cfg.scanEmbeddedStructures != null ? cfg.scanEmbeddedStructures : null);
        setProfileEnabled(cfg.profileEnabled !== false);
        setOrder(buildInitialOrder(cfg.entityOrder));
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load entity level config');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return KNOWN_ENTITY_CODES
      .map((code) => ({ code, label: uiLabel(code) }))
      .filter((r) => !q || r.code.toLowerCase().includes(q) || r.label.toLowerCase().includes(q))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filter]);

  const levelOf = (code) => levels[code] || LEVEL_NORMAL;

  const setLevel = (code, level) => {
    setMessage(null);
    setLevels((prev) => {
      const next = { ...prev };
      if (level === LEVEL_NORMAL) delete next[code];
      else next[code] = level;
      return next;
    });
  };

  const applyToAll = (level) => {
    setMessage(null);
    if (level === LEVEL_NORMAL) {
      setLevels({});
      return;
    }
    const next = {};
    KNOWN_ENTITY_CODES.forEach((code) => { next[code] = level; });
    setLevels(next);
  };

  const moveEntity = (index, dir) => {
    setMessage(null);
    setOrder((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const resetOrder = () => {
    setMessage(null);
    setOrder(buildInitialOrder(null));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await saveAdvancedConfig({
        entityLevels: levels,
        entityTypeToAlgorithm: algoOverride,
        maxRowsPerTable,
        scanEmbeddedStructures,
        profileEnabled,
        entityOrder: order,
      });
      setMessage('Saved. Changes apply to all users on their next page load.');
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const modifiedCount = Object.keys(levels).length;

  return (
    <div>
      <h1 className='adv-config-screen-title'>Entity Levels</h1>
      <p className='adv-config-screen-desc'>
        Control how each detected entity type is treated across the product, install-wide.
        <strong> Normal</strong> entities appear in reports and exports; <strong>Report Only</strong>{' '}
        entities appear in reports but are excluded from exports; <strong>Hidden</strong> entities
        are excluded from both. This is a front-end display setting only — it does not change what
        discovery detects or how masking runs.
      </p>

      <div className='adv-config-card'>
        <div className='adv-config-toolbar'>
          <input
            data-testid='filter-adv-config-entities'
            className='adv-config-search'
            placeholder='Filter entities…'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span style={{ flex: 1 }} />
          {canEdit && (
            <>
              <span style={{ fontSize: 12, color: INK[500] }}>Set all:</span>
              {ENTITY_LEVELS.map((lvl) => (
                <button key={lvl} type='button' className='adv-config-btn-secondary adv-config-btn'
                  onClick={() => applyToAll(lvl)} disabled={saving}>
                  {labelForLevel(lvl)}
                </button>
              ))}
            </>
          )}
        </div>

        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className='adv-config-table'>
            <thead>
              <tr>
                <th style={{ width: '45%' }}>Entity</th>
                <th style={{ width: '20%' }}>Level</th>
                <th>Effect</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ code, label }) => {
                const lvl = levelOf(code);
                return (
                  <tr key={code}>
                    <td>
                      {label}
                      <span style={{ color: INK[400], marginLeft: 8, fontSize: 11 }}>{code}</span>
                      {lvl !== LEVEL_NORMAL && <span className='adv-config-badge-modified'>{labelForLevel(lvl)}</span>}
                    </td>
                    <td>
                      <select className='adv-config-select' value={lvl} disabled={!canEdit || saving}
                        onChange={(e) => setLevel(code, e.target.value)}>
                        {ENTITY_LEVELS.map((l) => (
                          <option key={l} value={l}>{labelForLevel(l)}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ color: INK[500], fontSize: 12 }}>{LEVEL_HELP[lvl]}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={3} style={{ color: INK[500] }}>No entities match “{filter}”.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <h1 className='adv-config-screen-title' style={{ marginTop: 28 }}>Entity Priority Order</h1>
      <p className='adv-config-screen-desc'>
        Sets the <strong>“most important PII first”</strong> order for entity-type displays such as
        the Data Privacy Workflow “Entity types found” tiles. Reorder with the arrows; the top of
        the list is shown first. Front-end display only — it does not change detection or masking.
      </p>

      <div className='adv-config-card'>
        <div className='adv-config-toolbar'>
          <span style={{ fontSize: 12, color: INK[500] }}>{order.length} entities</span>
          <span style={{ flex: 1 }} />
          {canEdit && (
            <button type='button' className='adv-config-btn-secondary adv-config-btn'
              onClick={resetOrder} disabled={saving}>
              Reset to default
            </button>
          )}
        </div>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            <table className='adv-config-table'>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Entity</th>
                  <th style={{ width: 110 }}>Order</th>
                </tr>
              </thead>
              <tbody>
                {order.map((code, index) => (
                  <tr key={code}>
                    <td style={{ color: INK[400] }}>{index + 1}</td>
                    <td>
                      {uiLabel(code)}
                      <span style={{ color: INK[400], marginLeft: 8, fontSize: 11 }}>{code}</span>
                    </td>
                    <td>
                      <button type='button' className='adv-config-btn-secondary adv-config-btn'
                        onClick={() => moveEntity(index, -1)}
                        disabled={!canEdit || saving || index === 0}
                        aria-label={`Move ${uiLabel(code)} up`} title='Move up'>
                        ▲
                      </button>
                      <button type='button' className='adv-config-btn-secondary adv-config-btn'
                        onClick={() => moveEntity(index, 1)}
                        disabled={!canEdit || saving || index === order.length - 1}
                        aria-label={`Move ${uiLabel(code)} down`} title='Move down'>
                        ▼
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='adv-config-toolbar'>
        <button type='button' className='adv-config-btn' onClick={save} disabled={!canEdit || saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <span style={{ fontSize: 12, color: INK[500] }}>
          {modifiedCount === 0 ? 'All entities Normal' : `${modifiedCount} entit${modifiedCount === 1 ? 'y' : 'ies'} customized`}
        </span>
        {message && <span className='adv-config-msg-ok'>{message}</span>}
        {error && <span className='adv-config-msg-err'>{error}</span>}
      </div>
    </div>
  );
};

function labelForLevel(level) {
  switch (level) {
    case 'REPORT_ONLY': return 'Report Only';
    case 'HIDDEN': return 'Hidden';
    default: return 'Normal';
  }
}

export default EntityLevelsScreen;
